import React, { useState, useEffect } from "react";
import "./Settings.css";
import useApi from "../../../../utils/api-manager/Helper/useApi";
import { ToastContainer, toast } from "react-toastify";
import Cart from '../../../../assets/SubscriptionImage/Cart.png';
import SubscriptionModal from "../../AppPAges/SubscriptionUI/SubscriptionModal";
import SubscriptionPage from "../SubscriptionUI/SubscriptionPage";
import PaymentPage from "../SubscriptionUI/PaymentPage";
import { useNavigate } from "react-router";
import { renderModuleIcon } from '../../../Pages/AppPAges/SubscriptionUI/moduleIcons';
import useFormattingUtils from "../../../../context/useFormattingUtils";


function StoreInformation() {
  const [showModal, setShowModal] = useState(false);
  const [plans, setPlans] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [subscriptionEntryData, setSubscriptionEntryData] = useState([]);
  const { getAPI, _Get } = useApi();
  const [modelData, setModelData] = useState({});
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [showingPaymentPage, setShowingPaymentPage] = useState(false);
  const [selectedModulesForPayment, setSelectedModulesForPayment] = useState([]);
  const [selectedBillingCycle, setSelectedBillingCycle] = useState(null);
  // const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [hasAvailableModules, setHasAvailableModules] = useState(true);
  const [allFeatures, setAllFeatures] = useState([]);
  const navigate = useNavigate();
  const { formatCurrency, formatDate } = useFormattingUtils();

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [userResponse, featuresResponse] = await Promise.all([
          _Get("user", { timeout: 10000 }),
          _Get("featuresWithPrices")
        ]);

        if (userResponse?.results?.length > 0) {
          setUser(userResponse.results[0]);
        }

        if (featuresResponse) {
          setAllFeatures(featuresResponse);
          evaluateAvailableModules(featuresResponse);
        }

        await fetchPlans();
        await fetchSubscriptionInvoices();
      } catch (error) {
        console.error("Error fetching data:", error);
        toast.error("Error fetching data.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const evaluateAvailableModules = (features) => {
    const allowedFeatures = JSON.parse(localStorage.getItem("allowedFeatures")) || [];

    const availableModules = features.filter(item => {
      if (!item.enable) return false;

      const title = item.title.toLowerCase();
      if (title.includes('+')) {
        const parts = title.split('+').map(f => f.trim().toLowerCase());
        return parts.every(part => !allowedFeatures.some(af => af.toLowerCase() === part));
      }

      return !allowedFeatures.some(af => af.toLowerCase() === title);
    });

    setHasAvailableModules(availableModules.length > 0);
  };

  const fetchPlans = async () => {
    try {
      const response = await _Get("remainingTime");
      const { latest_invoices } = response;

      if (Array.isArray(latest_invoices)) {
        const formattedPlans = latest_invoices.map((invoice) => ({
          id: invoice.feature_id,
          title: invoice.feature_entry_title,
          endDate: invoice.end_date,
          remainingDays: invoice.remaining_days,
          amount: invoice.amount || 0,
          is_active: invoice.is_active || false,
          enable: true,
          inc_features_titles: invoice.inc_features_titles || [],
        }));

        setPlans(formattedPlans);
        setSubscriptions(formattedPlans);
      } else {
        console.error("Invalid data format for latest_invoices:", latest_invoices);
      }
    } catch (error) {
      console.error("Error fetching subscription data:", error);
      toast.error("Failed to load subscription data");
    }
  };

  const fetchSubscriptionInvoices = async () => {
    try {
      const response = await _Get("subscriptionInvoiceByStore");

      if (Array.isArray(response.results)) {
        const formattedInvoices = response.results.map((invoice) => {
          const titlesText = invoice.feature_entriey_invocies
            .map(item => item.feature_title)
            .join(", ");

          return {
            id: invoice.id,
            date: invoice.date,
            title: titlesText,
            total_amount: invoice.total_amount,
            feature_entriey_invocies: invoice.feature_entriey_invocies,
            raw_invoice: invoice,
            store_info: response.store_info
          };
        });

        setSubscriptionEntryData(formattedInvoices);
      } else {
        console.error("Unexpected response format:", response.results);
        toast.error("Failed to fetch subscription invoices.");
      }
    } catch (error) {
      console.error("Error fetching subscription invoices:", error);
      toast.error("Error fetching subscription invoices.");
    }
  };

  const handleRenew = async (featureId) => {
    try {
      setLoading(true);
      const response = await _Get(`${getAPI("featuresWithPrices")}?id=${featureId}`);

      if (response) {
        setModelData(response);
        setShowModal(true);
      } else {
        toast.error("No pricing information found for this feature.");
      }
    } catch (error) {
      console.error("Error fetching feature prices:", error);
      toast.error("Failed to get pricing information. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setShowModal(false);
    fetchPlans();
  };

  const handleViewBilling = (invoice) => {
    try {
      navigate('/subscriptionInvoice', {
        state: {
          invoiceData: {
            results: [invoice.raw_invoice],
            store_info: invoice.store_info
          }
        }
      });
    } catch (error) {
      console.error("Error handling invoice:", error);
      toast.error("Failed to load invoice details");
    }
  };

  const handleBuyNew = () => {
    setShowSubscriptionModal(true);
    setShowingPaymentPage(false);
  };

  const activeSubscriptions = (subscriptions || []).filter((plan) => plan.is_active).length;
  const expiringSoon = (subscriptions || []).filter((plan) => Number(plan.remainingDays) <= 7).length;
  const totalSubscriptionSpend = subscriptionEntryData.reduce(
    (total, invoice) => total + Number(invoice.total_amount || 0),
    0
  );

  return (
    <>
      <div className="store-info-page">
        <ToastContainer />
        <SubscriptionModal
          show={showModal}
          handleClose={handleClose}
          modelData={modelData}
          width="50%"
          height="auto"
        />

        <section className="store-info-shell">
          <div className="store-info-hero">
            <div>
              <span className="store-info-kicker">Store workspace</span>
              <h1>Store Information</h1>
              <p>Review your active store, subscription modules and billing history from one clean workspace.</p>
            </div>
            <button
              className="store-info-buy-btn"
              disabled={!hasAvailableModules}
              onClick={handleBuyNew}
              title={!hasAvailableModules ? "All available modules are already active" : "Buy a new module"}
            >
              <img src={Cart} alt="" />
              Buy New
            </button>
          </div>

          <div className="store-info-profile-card">
            <div className="store-info-avatar">
              {(user?.selected_store_name || "PV").slice(0, 2).toUpperCase()}
            </div>
            <div className="store-info-profile-main">
              <span>Active Store</span>
              <h2>{user?.selected_store_name || "Store name not available"}</h2>
              <p>{user?.email || "Store email not available"}</p>
            </div>
            <div className="store-info-profile-meta">
              <span>Store ID</span>
              <strong>{user?.store || "N/A"}</strong>
            </div>
          </div>

          <div className="store-info-stats-grid">
            <div className="store-info-stat-card">
              <span>Active Modules</span>
              <strong>{activeSubscriptions}</strong>
              <p>Currently enabled for this store</p>
            </div>
            <div className="store-info-stat-card">
              <span>Expiring Soon</span>
              <strong>{expiringSoon}</strong>
              <p>Modules with 7 or fewer days left</p>
            </div>
            <div className="store-info-stat-card">
              <span>Billing Records</span>
              <strong>{subscriptionEntryData.length}</strong>
              <p>Subscription invoices loaded</p>
            </div>
            <div className="store-info-stat-card store-info-stat-card-dark">
              <span>Total Paid</span>
              <strong>{formatCurrency(totalSubscriptionSpend)}</strong>
              <p>From loaded subscription invoices</p>
            </div>
          </div>

          <section className="store-info-section">
            <div className="store-info-section-header">
              <div>
                <span className="store-info-section-label">Subscriptions</span>
                <h2>Active Subscriptions</h2>
                <p>Renew modules before they expire or add new apps when your store needs them.</p>
              </div>
            </div>

            {loading ? (
              <div className="store-info-empty-state">Loading store subscription details...</div>
            ) : subscriptions?.length ? (
              <div className="store-info-subscription-list">
                {subscriptions.map((plan, index) => (
                  <article key={`${plan.id}-${index}`} className="store-info-subscription-card">
                    <div className="store-info-module-title">
                      <div className="store-info-module-icon">{renderModuleIcon(plan.title)}</div>
                      <div>
                        <h3>{plan.title || "Unknown module"}</h3>
                        <span className={plan.is_active ? "store-info-status active" : "store-info-status"}>
                          {plan.is_active ? "Active" : "Inactive"}
                        </span>
                      </div>
                    </div>

                    <div className="store-info-plan-meta">
                      <div>
                        <span>Active till</span>
                        <strong>{formatDate(plan.endDate)}</strong>
                      </div>
                      <div>
                        <span>Remaining</span>
                        <strong>{plan.remainingDays ?? 0} days</strong>
                      </div>
                      <div>
                        <span>Amount</span>
                        <strong>{formatCurrency(plan.amount || 0)}</strong>
                      </div>
                    </div>

                    <button className="store-info-renew-btn" onClick={() => handleRenew(plan.id)}>
                      Renew
                    </button>
                  </article>
                ))}
              </div>
            ) : (
              <div className="store-info-empty-state">
                No active subscriptions found. Use Buy New to add modules for this store.
              </div>
            )}
          </section>

          <section className="store-info-section store-info-invoice-section">
            <div className="store-info-section-header">
              <div>
                <span className="store-info-section-label">Billing</span>
                <h2>Invoices</h2>
                <p>Open previous subscription invoices for account review and billing records.</p>
              </div>
            </div>
            <div className="store-info-table-wrap">
              <table className="store-info-invoice-table">
                <thead>
                  <tr>
                    <th>Invoice</th>
                    <th>Modules</th>
                    <th>Date</th>
                    <th>Amount Paid</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {subscriptionEntryData.length ? (
                    subscriptionEntryData.map((invoice, index) => (
                      <tr key={invoice.id || index}>
                        <td>
                          <strong>#{String(invoice.id || "").slice(0, 8).toUpperCase()}</strong>
                          <span>Subscription billing</span>
                        </td>
                        <td>
                          <div className="store-info-invoice-modules">{invoice.title || "No modules"}</div>
                        </td>
                        <td>{formatDate(invoice.date)}</td>
                        <td className="store-info-invoice-amount">{formatCurrency(invoice.total_amount || 0)}</td>
                        <td>
                          <button className="store-info-billing-btn" onClick={() => handleViewBilling(invoice)}>
                            View Bill
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5">
                        <div className="store-info-empty-state">No subscription invoices found.</div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </section>
      </div>

      {showSubscriptionModal && (
        <div
          className="store-info-modal-overlay"
        >
          <div
            className="store-info-modal-panel"
          >
            {!showingPaymentPage && (
              <button
                className="store-info-modal-close"
                onClick={() => setShowSubscriptionModal(false)}
              >
                x
              </button>
            )}

            <div>
              {!showingPaymentPage ? (
                <SubscriptionPage
                  inModal={true}
                  onProceedToPayment={(selectedModules, billingCycle) => {
                    setSelectedModulesForPayment(selectedModules);
                    setSelectedBillingCycle(billingCycle);
                    setShowingPaymentPage(true);
                  }}
                  storeId={user?.store}
                  onAvailableModulesChange={setHasAvailableModules}
                />
              ) : (
                <PaymentPage
                  inModal={true}
                  selectedModules={selectedModulesForPayment}
                  billingCycle={selectedBillingCycle}
                  storeId={user?.store}
                  onBack={() => setShowingPaymentPage(false)}
                  onComplete={() => {
                    setShowSubscriptionModal(false);
                    setShowingPaymentPage(false);
                    fetchPlans();
                    fetchSubscriptionInvoices();
                    evaluateAvailableModules(allFeatures);
                  }}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default StoreInformation;
